import {
  SALE_STATUSES_MAP,
  PICKED_UP_BY_DRIVER,
  DELIVERED,
  READY_TO_COLLECT,
  RECEIVED_AT_STORE,
  PROCESSED_AT_STORE,
  ORDER_SUBMITTED,
} from '../../constants';

import { safeString, dollarsToCents } from '../misc';

import processReceiptItem from './processReceiptItem';

const processId = (a?: number) => (a ? String(a) : undefined);

const getOrderStatus = ({
  rawSale,
  pickupReady,
  status,
}: {
  rawSale: RawSale;
  pickupReady?: boolean;
  status: string;
}) => {
  // the additional delivery statuses are not in the same place
  // this code corrects that
  // Uber uses OrderStatus. DoorDash uses DeliveryStatus.
  const deliveryStatus = safeString(
    rawSale.DeliveryInfo?.OrderStatus || rawSale.DeliveryInfo?.DeliveryStatus,
  );

  if (deliveryStatus === 'delivered') {
    return SALE_STATUSES_MAP[DELIVERED];
  }
  // Different delivery providers use different terms for delivery status. Here we map them to equivalent Redcat statuses.
  if (
    deliveryStatus &&
    [
      'picked-up',
      'enroute-to-dropoff',
      'arrived-at-dropoff',
      'arrived-at-consumer',
    ].includes(deliveryStatus)
  ) {
    return SALE_STATUSES_MAP[PICKED_UP_BY_DRIVER];
  }
  if (status === 'scheduled') {
    return SALE_STATUSES_MAP[RECEIVED_AT_STORE];
  }
  if (status === 'future-dated') {
    return SALE_STATUSES_MAP[ORDER_SUBMITTED];
  }
  if (status === 'enroute-to-pickup') {
    return SALE_STATUSES_MAP[PROCESSED_AT_STORE];
  }
  if (deliveryStatus && status === 'ready-for-collection') {
    return SALE_STATUSES_MAP[PROCESSED_AT_STORE];
  }
  if (!deliveryStatus && pickupReady) {
    return SALE_STATUSES_MAP[READY_TO_COLLECT];
  }
  return SALE_STATUSES_MAP[status];
};

export default function processSale(
  rawSale: RawSale,
  pickupReady?: boolean,
): Sale {
  let status = safeString(rawSale.Status);

  return {
    status: getOrderStatus({ rawSale, pickupReady, status }),
    folioId: processId(rawSale.FolioID),
    transactionId: processId(rawSale.TransactionID || rawSale.ID),
    orderId: processId(rawSale.OrderID),
    headerId: processId(rawSale.HeaderID),
    pickupTime: rawSale.PickupTime,
    inError: Boolean(rawSale.InError),
    deliveryTrackingUrl: rawSale?.DeliveryInfo?.DeliveryTrackingUrl,
    estimatedDeliveryTime:
      rawSale?.DeliveryInfo?.EstimatedDeliveryTime ||
      rawSale?.EstimatedDeliveryTime,
    items: (rawSale.Items || []).map(processReceiptItem),
    total: rawSale.Total != null ? dollarsToCents(rawSale.Total) : undefined,
    orderDate: rawSale.OrderDate,
    receiptType: rawSale.PrinterSettings?.KioskPrintReceiptType,
    storeId: rawSale.StoreID,
  };
}

// example sale details:

// "Address": {},
// "DeliveryTime": null,
// "FolioID": 5,
// "ID": 453,
// "Items": [
//     {
//         "Ingredients": [
//             {
//                 "Ingredients": [],
//                 "Item": "GF Wrap",
//                 "PLUCode": 151,
//                 "Price": 1.0,
//                 "Qty": 1.0,
//                 "RedeemPoints": 0.0
//             },
//             {
//                 "Ingredients": [],
//                 "Item": "#Chicken GF",
//                 "PLUCode": 114,
//                 "Price": 0.0,
//                 "Qty": 1.0,
//                 "RedeemPoints": 0.0
//             },
//             {
//                 "Ingredients": [

//                 ],
//                 "Item": "Make It A Meal",
//                 "PLUCode": 109,
//                 "Price": 0.0,
//                 "Qty": 1.0,
//                 "RedeemPoints": 0.0
//             },
//                                             {
//                         "Ingredients": [],
//                         "Item": "Sweet Potat REG",
//                         "PLUCode": 951,
//                         "Price": 5.4,
//                         "Qty": 1.0,
//                         "RedeemPoints": 0.0
//                     },
//                     {
//                         "Ingredients": [],
//                         "Item": "Cool Ridge",
//                         "PLUCode": 788,
//                         "Price": 3.2,
//                         "Qty": 1.0,
//                         "RedeemPoints": 0.0
//                     }
//         ],
//         "Item": "Americn Drm CHK",
//         "PLUCode": 180,
//         "Price": 12.9,
//         "Qty": 1.0,
//         "RedeemPoints": 0.0
//     }
// ],
// "OrderDate": "2020-10-30T13:54:16",
// "OrderName": "John Smith",
// "PaymentType": null,
// "PaymentTypeName": null,
// "PickupTime": "2020-10-30T14:04:14",
// "PosSaleID": null,
// "SaleType": 102,
// "Status": "Order Submitted",
// "StoreID": 4,
// "TableNo": null,
// "Total": 22.5
